import React from "react";
import { StyleSheet, View } from "react-native";
import { ClipboardTextIcon, KanbanIcon } from "@phosphor-icons/react";

import CustomHtml from "../../../../../components/common/general/CustomHtml/CustomHtml";
import CustomText from "../../../../../components/common/general/CustomText/CustomText";
import { colors } from "../../../../../styles/design-system";
import { ModernizedActivityEntityType } from "@axio/graphql/generated/graphql";
import type {
  ActivityDetail,
  CourseEvaluatorReviewItemType,
  CourseEvaluatorReviewSidebarItem,
} from "../types/reviewTypes";
import { COURSE_EVALUATOR_REVIEW_ITEM_TYPE_LABELS } from "../constants/itemTypeLabels";
import ActivityPanelTabs from "./ActivityPanelTabs";

interface ManualActivityPanelProps {
  selectedReviewItem: CourseEvaluatorReviewSidebarItem;
  activityDetail: ActivityDetail | null;
  isExpanded?: boolean;
}

function ManualActivityPanel({ selectedReviewItem, activityDetail, isExpanded = false }: ManualActivityPanelProps) {
  const description = activityDetail?.currentDescription?.trim();
  const isNew = selectedReviewItem.meta?.some((meta) => meta.kind === "tag" && meta.tag === "new");
  const isProject = selectedReviewItem.entityType === ModernizedActivityEntityType.Project;
  const itemTypeMeta = selectedReviewItem.meta?.find(
    (meta): meta is { kind: "itemType"; itemType: CourseEvaluatorReviewItemType } => meta.kind === "itemType"
  );
  const typeLabel = isProject
    ? "Project"
    : itemTypeMeta
      ? COURSE_EVALUATOR_REVIEW_ITEM_TYPE_LABELS[itemTypeMeta.itemType]
      : "Assignment";

  return (
    <View style={styles.panel}>
      {!isExpanded && (
        <>
          <View style={styles.badgesRow}>
            {isNew ? (
              <View style={styles.newBadge}>
                <CustomText
                  text="New"
                  useTranslationText={false}
                  size="xs"
                  weight="medium"
                  style={styles.newBadgeText}
                />
              </View>
            ) : null}
            <View style={styles.typeBadge}>
              {isProject ? (
                <KanbanIcon size={13} color={colors.brand[600]} weight="fill" />
              ) : (
                <ClipboardTextIcon size={13} color={colors.brand[600]} weight="fill" />
              )}
              <CustomText
                text={typeLabel}
                useTranslationText={false}
                size="xs"
                weight="medium"
                style={styles.typeBadgeText}
              />
            </View>
            <View style={styles.manualLabel}>
              <CustomText
                text="Manually added activity"
                useTranslationText={false}
                size="xs"
                style={styles.manualLabelText}
              />
            </View>
          </View>

          <CustomText
            text={selectedReviewItem.title}
            useTranslationText={false}
            size="xl"
            weight="bold"
            style={styles.title}
          />
        </>
      )}

      <ActivityPanelTabs attachments={activityDetail?.attachments}>
        <View style={styles.content}>
          <View style={styles.card}>
            <CustomText
              text="About this activity"
              useTranslationText={false}
              size="s"
              weight="bold"
              style={styles.cardTitle}
            />
            <CustomText
              text="Architect-authored addition."
              useTranslationText={false}
              size="xs"
              style={styles.bodyText}
            />
            <View style={styles.divider} />
            <View style={styles.inlineText}>
              <CustomText
                text="Learning objective:"
                useTranslationText={false}
                size="xs"
                weight="medium"
                style={styles.bodyText}
              />
              <CustomText
                text="Authored by the course architect."
                useTranslationText={false}
                size="xs"
                style={styles.mutedText}
              />
            </View>
          </View>

          {description ? (
            <View style={styles.card}>
              <CustomText
                text="Description"
                useTranslationText={false}
                size="s"
                weight="bold"
                style={styles.cardTitle}
              />
              <CustomHtml html={description} style={styles.description} />
            </View>
          ) : null}
        </View>
      </ActivityPanelTabs>
    </View>
  );
}

const styles = StyleSheet.create({
  panel: {
    gap: 16,
  },
  badgesRow: {
    flexDirection: "row",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 8,
  },
  newBadge: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 6,
    backgroundColor: "#E7F7F8",
  },
  newBadgeText: {
    color: "#15727D",
  },
  typeBadge: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 6,
    backgroundColor: colors.brand[50],
  },
  typeBadgeText: {
    color: colors.brand[600],
  },
  manualLabel: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  manualLabelText: {
    color: colors.grey[400],
  },
  title: {
    color: colors.grey[900],
  },
  content: {
    gap: 16,
  },
  card: {
    padding: 16,
    gap: 10,
    borderWidth: 1,
    borderColor: colors.grey[200],
    borderRadius: 16,
    backgroundColor: colors.white,
  },
  cardTitle: {
    color: colors.grey[900],
  },
  bodyText: {
    color: colors.grey[700],
    lineHeight: 18,
  },
  mutedText: {
    color: colors.grey[500],
    lineHeight: 18,
  },
  divider: {
    height: 1,
    backgroundColor: colors.grey[200],
  },
  inlineText: {
    flexDirection: "row",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 4,
  },
  description: {
    color: colors.grey[700],
    fontFamily: "Inter",
    fontSize: 13,
    lineHeight: 20,
  },
});

export default ManualActivityPanel;
